class IvviReviewsCarousel extends HTMLElement {
  constructor() {
    super();

    this.SELECTORS = {
      swiper: '[data-reviews-swiper]',
      prev: '[data-swiper-prev]',
      next: '[data-swiper-next]',
      scrollbar: '[data-swiper-scrollbar]',
      media: '.ivvi-review-card__media',
      video: '.ivvi-review-card__video',
      button: '[data-video-toggle]',
      slide: '.swiper-slide',
      placeholder: '.ivvi-review-card__video-placeholder',
    };

    this.swiper = null;
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
  }

  connectedCallback() {
    if (!window.Swiper) return;
    this.init();
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.pauseAllVideos();
    this.destroySwiper();
  }

  isMobile() {
    return window.innerWidth < 1280;
  }

  getSwiperElement() {
    return this.querySelector(this.SELECTORS.swiper);
  }

  updateVideoButtonState(video) {
    const media = video.closest(this.SELECTORS.media);
    if (!media) return;

    const button = media.querySelector(this.SELECTORS.button);
    if (!button) return;

    if (video.paused) {
      button.classList.remove('tw:hidden');
    } else {
      button.classList.add('tw:hidden');
    }
  }

  pauseAllVideos(exceptVideo) {
    const videos = this.querySelectorAll(this.SELECTORS.video);

    videos.forEach((video) => {
      if (exceptVideo && video === exceptVideo) return;

      if (!video.paused) {
        video.pause();
      }

      this.updateVideoButtonState(video);
    });
  }

  playVideo(video) {
    if (!video) return;

    this.pauseAllVideos(video);

    const playPromise = video.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          this.hideVideoPlaceholder(video);
          this.updateVideoButtonState(video);
        })
        .catch(() => {
          this.updateVideoButtonState(video);
        });
    } else {
      this.hideVideoPlaceholder(video);
      this.updateVideoButtonState(video);
    }
  }

  pauseVideo(video) {
    if (!video) return;

    video.pause();
    this.updateVideoButtonState(video);
  }

  toggleVideo(video) {
    if (!video) return;

    if (video.paused) {
      this.playVideo(video);
    } else {
      this.pauseVideo(video);
    }
  }

  bindVideoEvents() {
    const mediaItems = this.querySelectorAll(this.SELECTORS.media);

    mediaItems.forEach((media) => {
      const video = media.querySelector(this.SELECTORS.video);
      const button = media.querySelector(this.SELECTORS.button);

      if (!video || !button) return;

      video.controls = false;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      this.updateVideoButtonState(video);

      if (video.dataset.eventsBound !== 'true') {
        video.dataset.eventsBound = 'true';

        video.addEventListener('play', () => {
          this.hideVideoPlaceholder(video);
          this.updateVideoButtonState(video);
        });

        video.addEventListener('pause', () => {
          this.updateVideoButtonState(video);
        });

        video.addEventListener('ended', () => {
          video.currentTime = 0;
          this.updateVideoButtonState(video);
        });
      }

      if (button.dataset.eventsBound !== 'true') {
        button.dataset.eventsBound = 'true';

        button.addEventListener('pointerup', (event) => {
          if (!this.isMobile()) return;

          event.preventDefault();
          event.stopPropagation();

          this.toggleVideo(video);
        });

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (this.isMobile()) return;

          this.toggleVideo(video);
        });
      }
    });
  }

  destroySwiper() {
    const swiperEl = this.getSwiperElement();

    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }

    this.swiper = null;
  }

  hideVideoPlaceholder(video) {
    const media = video.closest(this.SELECTORS.media);
    if (!media) return;

    const placeholder = media.querySelector(this.SELECTORS.placeholder);
    if (!placeholder) return;

    placeholder.classList.add('tw:hidden!');
  }

  initSwiper() {
    const swiperEl = this.getSwiperElement();
    if (!swiperEl) return;

    this.destroySwiper();

    this.swiper = new window.Swiper(swiperEl, {
      loop: true,
      speed: 700,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      noSwiping: true,
      noSwipingClass: 'swiper-no-swiping',
      slidesPerView: 'auto',
      centeredSlides: false,
      spaceBetween: 24,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: false,
      },
      keyboard: {
        enabled: true,
      },
      navigation: {
        prevEl: this.querySelector(this.SELECTORS.prev),
        nextEl: this.querySelector(this.SELECTORS.next),
      },
      scrollbar: {
        el: this.querySelector(this.SELECTORS.scrollbar),
        draggable: true,
      },
      breakpoints: {
        0: {
          spaceBetween: 16,
        },
        750: {
          spaceBetween: 24,
        },
        990: {
          spaceBetween: 24,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
        },
      },
      on: {
        init: () => {
          this.bindVideoEvents();
        },
        touchStart: () => {
          this.pauseAllVideos();
        },
        sliderMove: () => {
          this.pauseAllVideos();
        },
        transitionStart: () => {
          this.pauseAllVideos();
        },
      },
    });

    this.bindVideoEvents();
  }

  init() {
    this.initSwiper();
  }

  handleBlockSelect(event) {
    const target = event.target;
    if (!target || !this.contains(target)) return;

    this.pauseAllVideos();

    const swiperEl = this.getSwiperElement();
    if (!swiperEl || !swiperEl.swiper) return;

    const slideEl = target.closest(this.SELECTORS.slide);
    if (!slideEl) return;

    const realIndex = Number(slideEl.getAttribute('data-swiper-slide-index'));

    if (!Number.isNaN(realIndex)) {
      swiperEl.swiper.slideToLoop(realIndex);
    }
  }
}

if (!customElements.get('ivvi-reviews-carousel')) {
  customElements.define('ivvi-reviews-carousel', IvviReviewsCarousel);
}
